import { useCallback, useEffect, useRef, useState } from 'react';
import { HStack, Link, Stub, Switcher, Text, VStack } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { useLoadingOrder, useUpdateOrderRelatedServicesMutation } from '@/entities/order';
import { type SchemaRelatedServicesInfo } from '@/shared/api/openApi';
import { useMessageToast } from '@/shared/lib/hooks';
import { makeModifierClassname } from '@/shared/lib/makeModifierClassname';
import { AliceRelatedServicesModal } from './components/AliceRelatedServicesModal';
import styles from './AliceRelatedServices.module.css';

const useWasEverEnabled = (isDisabled: boolean) => {
    const [wasEverEnabled, setWasEverEnabled] = useState(!isDisabled);

    useEffect(() => {
        if (!isDisabled && !wasEverEnabled) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Автоматический игнор в рамках DIRECT-288723
            setWasEverEnabled(true);
        }
    }, [isDisabled, wasEverEnabled]);

    return wasEverEnabled;
};

const useOptimisticSwitcher = (serverValue: boolean) => {
    const [localValue, setLocalValue] = useState(serverValue);

    useEffect(() => {
        setLocalValue(serverValue);
    }, [serverValue]);

    return [localValue, setLocalValue] as const;
};

const useShowToast = (isEnabled: boolean, isDisabled: boolean) => {
    const wasEnabledAndAvailableRef = useRef<boolean | null>(null);

    const { showToast } = useMessageToast();

    useEffect(() => {
        if (wasEnabledAndAvailableRef.current && isDisabled) {
            showToast({
                title: 'Услуга по установке недоступна',
                caption: (
                    <span>
                        Ваш адрес доставки не входит в зону
                        <br /> обслуживания (Москва, Санкт-Петербург,
                        <br /> Московская и Ленинградская области)
                    </span>
                ),
            });
        }

        wasEnabledAndAvailableRef.current = !isDisabled && isEnabled;
    }, [isDisabled, isEnabled, showToast]);
};

interface AliceRelatedServicesProps {
    orderId: string;
    signature: string;
    storeSlug: string;
    relatedServiceInfo: SchemaRelatedServicesInfo;
}

const modifier = makeModifierClassname(styles, 'container');

export const AliceRelatedServices = (props: AliceRelatedServicesProps) => {
    const { orderId, signature, storeSlug, relatedServiceInfo } = props;

    const { isOpen, setFalsy: closeModal, setTruthy: openModal } = useToggle();

    const { isOrderUpdating, isOrderSideUpdating } = useLoadingOrder();

    const [updateRelatedServices, { isLoading: isLoadingServices }] = useUpdateOrderRelatedServicesMutation();

    const [optimisticEnabled, setOptimisticEnabled] = useOptimisticSwitcher(relatedServiceInfo.enabled);

    const handleUpdateRelatedServices = useCallback(
        (value: boolean) => {
            setOptimisticEnabled(value);
            updateRelatedServices({
                order_id: orderId,
                signature,
                store_slug: storeSlug,
                enabled: value,
                editable: relatedServiceInfo.editable,
                is_available_region: relatedServiceInfo.is_available_region,
            });
        },
        [orderId, relatedServiceInfo, setOptimisticEnabled, signature, storeSlug, updateRelatedServices]
    );

    const isDisabled = relatedServiceInfo.editable && !relatedServiceInfo.is_available_region;
    const wasEverEnabled = useWasEverEnabled(isDisabled);

    const isShow = relatedServiceInfo.editable && wasEverEnabled;
    const isShowStub = (isOrderUpdating || isOrderSideUpdating) && !isDisabled;

    useShowToast(relatedServiceInfo.enabled, isDisabled);

    if (!isShow) {
        return null;
    }

    return (
        <>
            <HStack alignItems="center" gap="16" className={modifier('disabled', isDisabled)}>
                <VStack alignItems="center" justifyContent="center" className={styles.switcher}>
                    {isShowStub ? (
                        <Stub height="20px" width="36px" borderRadius="14" />
                    ) : (
                        <Switcher
                            isSelected={optimisticEnabled}
                            isDisabled={isLoadingServices}
                            onChange={handleUpdateRelatedServices}
                        />
                    )}
                </VStack>

                <VStack gap="4" alignItems="flex-start">
                    <Text size="mc" bold>
                        Оставить заявку на установку
                    </Text>

                    <Link onClick={openModal}>
                        <Text size="mc" role="supplementary" className={styles.link}>
                            Подробнее
                        </Text>
                    </Link>
                </VStack>
            </HStack>

            <AliceRelatedServicesModal isOpen={isOpen} onClose={closeModal} />
        </>
    );
};
